import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  resource,
  signal,
  viewChild,
  viewChildren,
  ViewChild,
} from '@angular/core';
import { firstValueFrom, throwError } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilterEventService } from 'src/app/services/filter-event.service';
import { ContainerComponent } from 'src/app/common/components/analysis/shared/container/container.component';
import { Dashboard, DashboardService, Filter, FilterValue, Tab } from 'src/openapi-client';
import { FilterComponent, Type } from 'src/app/common/components/filter/filter.component';
import { TileComponent } from 'src/app/common/components/analysis/shared/tile/tile.component';
import { FilterSidepanelComponent } from './filter-sidepanel/filter-sidepanel.component';
import { SdDrawer, SdDropdown } from '@solid-design-system/components/dist-versioned';
import { ValueDisplayService, VALUE_DISPLAY_FILTER_KEY } from 'src/app/common/services/value-display.service';
import iconLibrary from 'src/app/common/iconLibrary';
import { TranslocoModule } from '@ngneat/transloco';
import { ExportDialogComponent, ExportDialogResult } from './export-dialog/export-dialog.component';
import {
  COPY_FORMATS,
  CopyFormat,
  EXPORT_FORMATS,
  ExportFormat,
  ExportRequest,
  ExportScope,
} from './export-dialog/export.model';
import {
  ImageExportDialogComponent,
  ImageExportDialogResult,
} from './image-export-dialog/image-export-dialog.component';
import {
  CapturedImage,
  IMAGE_EXPORT_FORMATS,
  ImageExportFormat,
  nextRender,
  PDF_EXPORT_FORMAT,
} from 'src/app/common/util/image-export';
import { buildFileName } from 'src/app/common/util/download';
import { DashboardExportActions } from './dashboard-export-actions';
import {
  TileExportFormat,
  TileExportHandler,
  TileExportRequest,
} from 'src/app/common/components/analysis/shared/tile/tile-export.handler';

const FUNDS_FILTER_KEY = 'funds';
const DATE_FILTER_KEY = 'date';
const SECURITIES_TAB_NAME = 'Wertpapiere';

/** Date for display, e.g. "2025-11-23" or "2025.11.23" → "23.11.2025". Unknown formats are returned unchanged. */
function toDisplayDate(date: string | undefined): string {
  const match = /^(\d{4})[-.](\d{2})[-.](\d{2})/.exec(date ?? '');
  return match ? `${match[3]}.${match[2]}.${match[1]}` : (date ?? '');
}

/** Date for file names, e.g. "2025.11.23" → "2025-11-23". */
function toFileNameDate(date: string | undefined): string {
  return (date ?? '').replace(/\./g, '-');
}

@Component({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-dashboard',
  imports: [
    ContainerComponent,
    FilterComponent,
    TileComponent,
    FilterSidepanelComponent,
    ExportDialogComponent,
    ImageExportDialogComponent,
    CommonModule,
    TranslocoModule,
  ],
  providers: [
    DashboardExportActions,
    { provide: TileExportHandler, useExisting: forwardRef(() => DashboardComponent) },
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent {
  private readonly dashboardService = inject(DashboardService);
  private readonly exportActions = inject(DashboardExportActions);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  @ViewChild('filterSidepanel') readonly filterSidepanel!: FilterSidepanelComponent;
  @ViewChild('appExportDialog') readonly exportDialog!: ExportDialogComponent;
  @ViewChild('appImageExportDialog') readonly imageExportDialog!: ImageExportDialogComponent;
  readonly exportArea = viewChild<ElementRef<HTMLElement>>('exportArea');

  readonly disabledDates =
    '2025.12.10,2025.12.11,,2025.12.12,2025.12.13,2025.12.14,2025.12.15,,2025.12.16,2025.12.17,2025.12.18,2025.12.19,2025.12.20,2025.12.21,2025.12.22,2025.12.23,2025.12.24,2025.12.25,2025.12.26,2025.12.27,2025.12.28,2025.12.29,2025.12.30,2025.12.31';
  readonly id = input.required<string>();

  readonly FilterType = Type;
  readonly filterValues = signal<FilterValue[]>([]);

  readonly filterDrawer = viewChild<SdDrawer>('filterDrawer');
  private readonly moreMenu = viewChild<ElementRef<SdDropdown>>('moreMenu');
  private readonly tiles = viewChildren(TileComponent);

  /**
   * The displayed table that "Kopieren" puts into the clipboard. Undefined while the tiles are
   * loading or if the table has no data rows (e.g. everything filtered out); the menu entry is
   * disabled then. The first row contains the column headers.
   */
  readonly copyableTable = computed(() =>
    this.tiles()
      .map(tile => tile.displayedTable())
      .find(rows => rows !== undefined && rows.length > 1)
  );

  protected readonly iconLibrary = iconLibrary;
  protected readonly exportFormats = EXPORT_FORMATS;
  protected readonly copyFormats = COPY_FORMATS;
  protected readonly imageExportFormats = IMAGE_EXPORT_FORMATS;
  /** The securities tab exports CSV/XLSX via backend, PDF is added there as the dashboard picture. */
  protected readonly pdfFormat = PDF_EXPORT_FORMAT;

  /** True while the dashboard is captured: shows the export header (logo, fund, analysis, date). */
  protected readonly capturing = this.exportActions.capturing;
  protected readonly exportLogo = this.exportActions.logo;
  private capturedImage?: CapturedImage;

  readonly activeTabName = signal<string | undefined>(undefined);

  readonly dashboardContent = resource<Dashboard, string | undefined>({
    params: () => {
      try {
        return this.id();
      } catch {
        return undefined;
      }
    },
    loader: ({ params }) =>
      params === undefined
        ? Promise.resolve(undefined as unknown as Dashboard)
        : firstValueFrom(this.dashboardService.loadDashboard(params)),
  });

  readonly tabs = computed(() => this.dashboardContent.value()?.tabs ?? []);

  readonly viewSwitch = computed<Filter>(() => {
    const tabNames = this.tabs().map(tab => tab.name ?? '');
    return {
      key: 'tab',
      available: tabNames,
      preselection: this.activeTabName() ? [this.activeTabName()] : tabNames,
      name: 'Art',
    };
  });

  readonly tabContent = resource<Tab, { dashboardId: string; tabId: string | undefined }>({
    params: () => {
      try {
        const dashboardId = this.id();
        const tabName = this.activeTabName();
        if (!dashboardId || !tabName) {
          return undefined;
        }
        return { dashboardId, tabId: tabName };
      } catch {
        return undefined;
      }
    },
    loader: ({ params }) => firstValueFrom(this.dashboardService.loadTabsFilter(params.dashboardId, params.tabId)),
  });

  private readonly pickedFund = signal<string | undefined>(undefined);

  constructor() {
    inject(FilterEventService)
      .resetEvent.pipe(takeUntilDestroyed())
      .subscribe(() => this.filterSidepanel?.resetAllFilters());
    effect(() => {
      const firstTab = this.tabs()[0];
      if (firstTab?.name && !this.activeTabName()) {
        this.activeTabName.set(firstTab.name);
      }
    });
  }

  readonly fundsFilter = computed(() => {
    const loadedFilters = this.tabContent.value()?.filters;
    const base = loadedFilters?.find(v => v.key === FUNDS_FILTER_KEY);
    if (!base) {
      return undefined;
    }
    const templateValues = this.filterValues().find(fv => fv.key === FUNDS_FILTER_KEY)?.values;
    return templateValues ? { ...base, preselection: templateValues } : base;
  });

  // signal<Filter>({
  //   key: 'funds',
  //   available: ['Gesamtportfolio', '618/ SAN', '2/SAN'],
  //   name: 'Fonds',
  //   preselection: ['Gesamtportfolio']
  // });

  readonly dateFilter = signal<Filter>({
    key: 'date',
    available: ['2025-11-23'],
    name: 'Datum',
    preselection: ['2025-11-23'],
  });

  readonly dateRange = signal(false);

  protected readonly valueDisplay = inject(ValueDisplayService);

  /** Backend-provided relative/absolute filter, preselected with the persisted value if still available. */
  readonly valueDisplayFilter = computed<Filter | undefined>(() => {
    const base = this.tabContent.value()?.filters?.find(v => v.key === VALUE_DISPLAY_FILTER_KEY);
    if (!base) {
      return undefined;
    }
    const stored = this.valueDisplay.value();
    const selected = stored !== null && base.available.includes(stored) ? stored : base.available[0];
    return { ...base, name: base.name ?? 'Art', preselection: selected === undefined ? [] : [selected] };
  });

  readonly valueDisplayLabel = computed(() => this.valueDisplayFilter()?.preselection?.[0]);

  changeValueDisplay(values: string[]): void {
    if (values[0] !== undefined) {
      this.valueDisplay.set(values[0]);
    }
  }

  /** filterValues + global value display; the latter is independent of filter/date changes. */
  readonly tileFilter = computed<FilterValue[]>(() => {
    const selected = this.valueDisplayLabel();
    return selected === undefined
      ? this.filterValues()
      : [...this.filterValues(), { key: VALUE_DISPLAY_FILTER_KEY, values: [selected] }];
  });

  readonly activeFilterCount = computed(() =>
    this.filterValues().reduce(
      (count, filter) =>
        count + (filter.key === DATE_FILTER_KEY || filter.key === FUNDS_FILTER_KEY ? 0 : (filter.values?.length ?? 0)),
      0
    )
  );

  readonly isSecuritiesTab = computed(() => this.activeTabName() === SECURITIES_TAB_NAME);
  readonly selectedFund = computed(() => this.pickedFund() ?? this.fundsFilter()?.preselection?.[0]);

  /** The date the data is shown for: the one picked in the date picker, otherwise its preselection. */
  readonly selectedDate = computed(
    () =>
      this.filterValues().find(filter => filter.key === DATE_FILTER_KEY)?.values?.[0] ??
      this.dateFilter().preselection?.[0]
  );

  readonly activeFilterLabels = computed(() =>
    this.filterValues()
      .filter(
        filter =>
          filter.key !== DATE_FILTER_KEY && filter.key !== FUNDS_FILTER_KEY && filter.key !== VALUE_DISPLAY_FILTER_KEY
      )
      .flatMap(filter => filter.values ?? [])
  );

  changeView(value: string[]): void {
    this.activeTabName.set(value[0]);
  }

  changeDate(value: string[]) {
    this.setFilterValue(this.dateFilter().key, value);
  }

  changeFunds(value: string[]) {
    this.pickedFund.set(value[0]);
    this.setFilterValue(FUNDS_FILTER_KEY, value);
  }

  openFilterPanel(): void {
    this.filterSidepanel?.open();
  }

  openSaveTemplateDialog(): void {
    this.filterSidepanel?.open();
    this.filterSidepanel?.openSaveTemplateDialog();
  }
  onFilterSelectionChange(selected: FilterValue[]): void {
    const dateFilter = this.filterValues().filter(filter => filter.key === DATE_FILTER_KEY);
    const fundsFilter = this.filterValues().filter(filter => filter.key === FUNDS_FILTER_KEY);
    this.filterValues.set([...dateFilter, ...fundsFilter, ...selected]);
  }

  /** The tile whose CSV/XLSX export waits for the choice in the dialog; undefined for the dashboard menu. */
  private pendingTileExport?: TileExportRequest;

  onExportClick(format: ExportFormat): void {
    this.pendingTileExport = undefined;
    if (this.activeFilterCount() > 0) {
      this.exportDialog?.open(format, this.activeFilterLabels(), this.selectedFund());
      return;
    }
    this.runExport(format, 'all');
  }

  onExportConfirmed(result: ExportDialogResult): void {
    if (this.pendingTileExport) {
      void this.exportActions.exportTile(
        this.tileExportRequest(this.pendingTileExport, result.format, result.scope),
        this.exportDialog
      );
      return;
    }
    this.runExport(result.format, result.scope, this.exportDialog);
  }

  /** Copies the displayed securities table into the clipboard (no backend call). */
  onCopyClick(format: CopyFormat): void {
    void this.exportActions.copyTable(this.copyableTable(), format);
  }

  /**
   * Dashboard export as PNG/PDF (Allokationen etc.). Captures everything currently visible - fund,
   * filters, date and absolute/relative display are part of the rendered dashboard - and opens the
   * preview dialog (PNG) or opens the browser's print dialog directly (PDF). Nothing is downloaded
   * before the user confirms.
   */
  async onImageExportClick(format: ImageExportFormat): Promise<void> {
    try {
      // Close the menu first and wait until it is really closed. The print dialog blocks the page, a
      // menu that is still closing would stay stuck (open, not hoverable) afterwards.
      await this.closeMoreMenu();
      this.capturedImage = await this.exportActions.captureImage(this.exportAreaElement());
      if (format === PDF_EXPORT_FORMAT) {
        await this.exportActions.printPdf(this.capturedImage, this.imageFileName());
        return;
      }
      this.imageExportDialog?.open(
        format,
        this.imageFileName(),
        this.capturedImage.previewUrl,
        this.activeFilterLabels()
      );
    } catch (error) {
      this.exportActions.reportExportError(format, error);
    }
  }

  /** Context menu of a single tile: PNG (preview dialog), PDF (print dialog), CSV/XLSX (backend, "current view"). */
  async exportTileAs(request: TileExportRequest, format: TileExportFormat): Promise<void> {
    if (format === 'CSV' || format === 'XLSX') {
      if (this.activeFilterCount() > 0) {
        // Scenario 2: filters are set, the user chooses between the current view and all data.
        this.pendingTileExport = request;
        this.exportDialog?.open(format, this.activeFilterLabels(), this.selectedFund());
        return;
      }
      // Scenario 1: no filters, download right away.
      await this.exportActions.exportTile(this.tileExportRequest(request, format, 'all'));
      return;
    }
    try {
      const fileName = buildFileName([...this.fileNameParts(), request.title], 'x', 'Analyse').replace(/\.x$/, '');
      this.capturedImage = await this.exportActions.captureTile(request.element, {
        header: [request.title, this.dashboardContent.value()?.name, this.selectedFund(), this.exportValueMode()]
          .filter(part => !!part)
          .join(' | '),
        footer: `Stichtag: ${this.exportDateLabel()}`,
      });
      if (format === PDF_EXPORT_FORMAT) {
        // A tile is scaled onto exactly one page.
        await this.exportActions.printPdf(this.capturedImage, fileName, true);
        return;
      }
      this.imageExportDialog?.open(format, fileName, this.capturedImage.previewUrl, this.activeFilterLabels());
    } catch (error) {
      this.exportActions.reportExportError(format, error);
    }
  }

  private tileExportRequest(request: TileExportRequest, format: ExportFormat, scope: ExportScope): ExportRequest {
    return {
      dashboardId: this.id(),
      fund: this.selectedFund(),
      format,
      // 'current': as displayed (all filters). 'all': independent of the narrowing filters, fund, Stichtag
      // and the value mode stay.
      scope,
      tileId: request.tileId,
      target: 'dashboard', // The dashboard menu exports the whole document (XLSX: all sheets), the tile menu one report.
      fileNameParts: [...this.fileNameParts(), request.title],
      filterValues: this.filterValues(),
    };
  }

  private async closeMoreMenu(): Promise<void> {
    const dropdown = this.moreMenu()?.nativeElement;
    if (!dropdown) {
      return;
    }
    await dropdown.hide();
    await nextRender();
  }

  onImageDownload(result: ImageExportDialogResult): void {
    this.exportActions.downloadImage(this.capturedImage, result.fileName, this.imageExportDialog);
  }

  onImageCopy(): Promise<void> {
    return this.exportActions.copyImage(this.capturedImage, this.imageExportDialog);
  }

  protected readonly exportDateLabel = computed(() => toDisplayDate(this.selectedDate()));

  protected readonly exportValueMode = this.valueDisplayLabel;

  private exportAreaElement(): HTMLElement {
    // Fallback to the DOM lookup in case the view query is not resolved (e.g. element not rendered yet).
    const element =
      this.exportArea()?.nativeElement ?? this.host.nativeElement.querySelector<HTMLElement>('.export-area');
    if (!element) {
      throw new Error('Das Dashboard ist nicht bereit für den Export');
    }
    return element;
  }

  private fileNameParts(): string[] {
    return [this.dashboardContent.value()?.name ?? '', this.selectedFund() ?? '', toFileNameDate(this.selectedDate())];
  }

  private imageFileName(): string {
    return buildFileName(this.fileNameParts(), 'x', 'Analyse').replace(/\.x$/, '');
  }

  private runExport(format: ExportFormat, scope: ExportScope, dialog?: ExportDialogComponent): void {
    // The report of the first tile of the tab (report index 0) is exported.
    const tileId = this.tabContent.value()?.tiles?.[0]?.id;
    void this.exportActions.exportTile(
      tileId
        ? {
            dashboardId: this.id(),
            fund: this.selectedFund(),
            format,
            scope,
            tileId,
            target: 'dashboard',
            fileNameParts: this.fileNameParts(),
            filterValues: this.filterValues(),
          }
        : undefined,
      dialog
    );
  }

  private setFilterValue(filterKey: string, values: string[]) {
    const existing = this.filterValues().find(filter => filter.key === filterKey);
    if (existing) {
      existing.values = values;
    } else {
      this.filterValues().push({ key: filterKey, values });
    }
    this.filterValues.set([...this.filterValues()]);
  }
}

